import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AnimatedCounter } from '../components/AnimatedCounter';
import type {
  FlowNodeRequest,
  ForumCategory,
  ForumThreadStatus,
  ForumThreadSummary
} from '../types';
import { useAuth } from '../context/AuthContext';
import '../styles/forum.css';

const PREVIEW_CHARACTER_LIMIT = 140;

function truncatePreview(value: string) {
  if (!value) {
    return '';
  }
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }
  if (normalized.length <= PREVIEW_CHARACTER_LIMIT) {
    return normalized;
  }
  return `${normalized.slice(0, PREVIEW_CHARACTER_LIMIT - 1)}…`;
}

const STATUS_OPTIONS: Array<{ label: string; value: ForumThreadStatus | 'all' }> = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Answered', value: 'answered' },
  { label: 'Closed', value: 'closed' }
];

const HIGHLIGHT_COUNT = 3;
const REQUESTS_CATEGORY_ID = '__requests__';

const REQUEST_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  under_development: 'Under development',
  done: 'Completed'
};

const REQUESTS_POLL_INTERVAL_MS = 15000;
const FLIP_ANIMATION_DURATION_MS = 420;
const FLIP_ANIMATION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

function findCategoryById(categories: ForumCategory[], id: string): ForumCategory | null {
  for (const category of categories) {
    if (category.id === id) {
      return category;
    }
    if (category.children && category.children.length > 0) {
      const fromChild = findCategoryById(category.children, id);
      if (fromChild) {
        return fromChild;
      }
    }
  }
  return null;
}

export function ForumPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isPageVisible, setIsPageVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'
  );

  const [threads, setThreads] = useState<ForumThreadSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [authorSearch, setAuthorSearch] = useState('');
  const [status, setStatus] = useState<ForumThreadStatus | 'all'>('all');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [showComposer, setShowComposer] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newTags, setNewTags] = useState('');
  const [newPinned, setNewPinned] = useState(false);
  const [newLocked, setNewLocked] = useState(false);
  const [newLockReason, setNewLockReason] = useState('');
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);
  const [hoverMode, setHoverMode] = useState<'question' | 'answer'>('question');
  const [threadPreviews, setThreadPreviews] = useState<
    Record<string, { question: string; answer?: string }>
  >({});
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const fetchedPreviewIds = useRef<Set<string>>(new Set());
  const [flowRequests, setFlowRequests] = useState<FlowNodeRequest[]>([]);
  const [requestSort, setRequestSort] = useState<'upvotes' | 'recent'>('upvotes');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestHasLoaded, setRequestHasLoaded] = useState(false);
  const [requestPolling, setRequestPolling] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestRefreshToken, setRequestRefreshToken] = useState(0);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestVoteBusy, setRequestVoteBusy] = useState<Record<string, boolean>>({});
  const [requestSearch, setRequestSearch] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [requestPage, setRequestPage] = useState(1);
  const [requestPageSize] = useState(10);
  const [requestTotalPages, setRequestTotalPages] = useState(1);
  const [requestTotalCount, setRequestTotalCount] = useState(0);
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [requestWithdrawBusy, setRequestWithdrawBusy] = useState<Record<string, boolean>>({});
  const [withdrawConfirmId, setWithdrawConfirmId] = useState<string | null>(null);
  const [doneFormId, setDoneFormId] = useState<string | null>(null);
  const [doneLinkInput, setDoneLinkInput] = useState('');
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>({});
  const requestCardRefs = useRef(new Map<string, HTMLLIElement>());
  const requestCardPositionsRef = useRef(new Map<string, DOMRect>());
  const prefersReducedMotionRef = useRef(false);
  const wasPageVisibleRef = useRef(isPageVisible);
  const requestRefreshReasonRef = useRef<'manual' | 'auto'>('manual');
  const triggerRequestRefresh = useCallback((reason: 'manual' | 'auto' = 'manual') => {
    requestRefreshReasonRef.current = reason;
    setRequestRefreshToken((value) => value + 1);
  }, []);

  const matchesRequestStatusFilter = useCallback(
    (request: FlowNodeRequest) => {
      if (requestStatusFilter === 'active') {
        return request.status !== 'done';
      }
      if (requestStatusFilter === 'completed') {
        return request.status === 'done';
      }
      return true;
    },
    [requestStatusFilter]
  );

  const orderRequests = useCallback(
    (items: FlowNodeRequest[]) => {
      const filtered = items.filter(matchesRequestStatusFilter);
      if (requestStatusFilter === 'completed') {
        return filtered;
      }
      const active = filtered.filter((request) => request.status !== 'done');
      const completed = filtered.filter((request) => request.status === 'done');
      return [...active, ...completed];
    },
    [matchesRequestStatusFilter, requestStatusFilter]
  );

  const registerRequestCard = useCallback(
    (id: string) => (node: HTMLLIElement | null) => {
      if (node) {
        requestCardRefs.current.set(id, node);
      } else {
        requestCardRefs.current.delete(id);
      }
    },
    []
  );

  const statusFilter = useMemo(() => (status === 'all' ? undefined : status), [status]);

  const flattenedCategories = useMemo(() => {
    const result: Array<{ id: string; name: string; depth: number }> = [];
    const walk = (nodes: ForumCategory[], depth = 0) => {
      nodes.forEach((node) => {
        result.push({ id: node.id, name: node.name, depth });
        if (node.children && node.children.length > 0) {
          walk(node.children, depth + 1);
        }
      });
    };
    walk(categories);
    return result;
  }, [categories]);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const data = await api.forumCategories();
        if (isMounted) {
          setCategories(data);
          setCategoryError(null);
        }
      } catch (err) {
        if (isMounted) {
          setCategoryError((err as Error).message);
        }
      }
    };

    loadCategories().catch(() => {
      /* handled above */
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState !== 'hidden');
    };
    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      prefersReducedMotionRef.current = mediaQuery.matches;
    };
    updatePreference();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }
    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);


  useEffect(() => {
    if (!hoveredThreadId) {
      setHoverMode('question');
      return;
    }
    const preview = threadPreviews[hoveredThreadId];
    if (!preview || !preview.answer) {
      setHoverMode('question');
      return;
    }

    setHoverMode('question');
    const intervalId = window.setInterval(() => {
      setHoverMode((current) => (current === 'question' ? 'answer' : 'question'));
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hoveredThreadId, threadPreviews]);

  useEffect(() => {
    if (flattenedCategories.length === 0) {
      setNewCategoryId('');
      return;
    }
    if (!flattenedCategories.some((category) => category.id === newCategoryId)) {
      setNewCategoryId(flattenedCategories[0].id);
    }
  }, [flattenedCategories, newCategoryId]);

  useEffect(() => {
    if (
      categoryFilter !== 'all' &&
      categoryFilter !== REQUESTS_CATEGORY_ID &&
      !flattenedCategories.some((category) => category.id === categoryFilter)
    ) {
      setCategoryFilter('all');
    }
  }, [categoryFilter, flattenedCategories]);

  useEffect(() => {
    const targetId = hoveredThreadId;
    if (!targetId) {
      return;
    }
    if (threadPreviews[targetId] || fetchedPreviewIds.current.has(targetId)) {
      return;
    }

    let isCancelled = false;
    fetchedPreviewIds.current.add(targetId);
    setLoadingPreviewId(targetId);

    const loadPreview = async () => {
      try {
        const detail = await api.forumThread(targetId);
        if (isCancelled) {
          return;
        }
        const topLevelPosts = Array.isArray(detail.posts) ? detail.posts : [];
        const questionPost =
          topLevelPosts.find((post) => !post.parentId) ?? topLevelPosts[0] ?? null;
        const answerPost =
          detail.answeredPostId && topLevelPosts.length > 0
            ? (function findAnswer(nodes: typeof topLevelPosts): typeof topLevelPosts[number] | null {
                for (const node of nodes) {
                  if (node.id === detail.answeredPostId) {
                    return node;
                  }
                  if (node.replies && node.replies.length > 0) {
                    const nested = findAnswer(node.replies);
                    if (nested) {
                      return nested;
                    }
                  }
                }
                return null;
              })(topLevelPosts)
            : null;

        setThreadPreviews((current) => ({
          ...current,
          [targetId]: {
            question: (questionPost?.content ?? '').trim() || detail.title || '',
            answer: answerPost && answerPost.content
              ? answerPost.content.trim()
              : undefined
          }
        }));
      } catch (err) {
        fetchedPreviewIds.current.delete(targetId);
      } finally {
        if (!isCancelled) {
          setLoadingPreviewId((current) => (current === targetId ? null : current));
        }
      }
    };

    loadPreview().catch(() => {
      setLoadingPreviewId((current) => (current === targetId ? null : current));
    });

    return () => {
      isCancelled = true;
      setLoadingPreviewId((current) => (current === targetId ? null : current));
    };
  }, [hoveredThreadId, threadPreviews]);

  useEffect(() => {
    if (!showComposer) {
      return;
    }
    if (categoryFilter !== 'all') {
      setNewCategoryId(categoryFilter);
    } else if (!newCategoryId && flattenedCategories.length > 0) {
      setNewCategoryId(flattenedCategories[0].id);
    }
  }, [showComposer, categoryFilter, flattenedCategories, newCategoryId]);

  useEffect(() => {
    if (!showComposer) {
      setNewPinned(false);
      setNewLocked(false);
      setNewLockReason('');
    }
  }, [showComposer]);

  useEffect(() => {
    if (categoryFilter === REQUESTS_CATEGORY_ID) {
      setThreads([]);
      setTotalThreads(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    let isActive = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.forumThreads({
          search: search.trim() || undefined,
          authorSearch: authorSearch.trim() || undefined,
          status: statusFilter,
          pinned: pinnedOnly || undefined,
          categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
          page,
          pageSize
        });
        if (!isActive) {
          return;
        }
        setThreads(response.items);
        setTotalThreads(response.total);
        setTotalPages(response.totalPages);
      } catch (err) {
        if (isActive) {
          setError((err as Error).message);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    run().catch(() => {
      /* handled above */
    });

    return () => {
      isActive = false;
    };
  }, [search, authorSearch, statusFilter, pinnedOnly, categoryFilter, page, pageSize]);

  useEffect(() => {
    if (categoryFilter !== REQUESTS_CATEGORY_ID) {
      setWithdrawConfirmId(null);
      setDoneFormId(null);
      setRequestLoading(false);
      setRequestPolling(false);
      setRequestHasLoaded(false);
      return;
    }
    let isMounted = true;
    const loadRequests = async () => {
      const refreshReason = requestRefreshReasonRef.current;
      if (refreshReason === 'manual') {
        setRequestLoading(true);
      } else {
        setRequestPolling(true);
      }
      setRequestError(null);
      try {
        const response = await api.forumRequests({
          sort: requestSort,
          search: requestSearch.trim() || undefined,
          status:
            requestStatusFilter === 'completed'
              ? 'completed'
              : requestStatusFilter === 'active'
              ? 'active'
              : undefined,
          createdBy: showMineOnly ? 'me' : undefined,
          page: requestPage,
          pageSize: requestPageSize
        });
        if (!isMounted) {
          return;
        }
        if (response.totalPages > 0 && requestPage > response.totalPages) {
          setRequestPage(response.totalPages);
          return;
        }
        setFlowRequests((current) => {
          const currentMap = new Map(current.map((request) => [request.id, request]));
          const merged = response.items.map((item) => {
            const existing = currentMap.get(item.id);
            return existing ? { ...existing, ...item } : item;
          });
          return orderRequests(merged);
        });
        setRequestHasLoaded(true);
        setRequestTotalPages(response.totalPages);
        setRequestTotalCount(response.total);
      } catch (err) {
        if (isMounted) {
          setRequestError((err as Error).message);
        }
      } finally {
        if (isMounted) {
          if (refreshReason === 'manual') {
            setRequestLoading(false);
          } else {
            setRequestPolling(false);
          }
          requestRefreshReasonRef.current = 'manual';
        }
      }
    };

    loadRequests().catch(() => {
      /* handled above */
    });

    return () => {
      isMounted = false;
    };
  }, [
    categoryFilter,
    requestSort,
    requestRefreshToken,
    requestSearch,
    requestStatusFilter,
    showMineOnly,
    requestPage,
    requestPageSize,
    orderRequests
  ]);

  useEffect(() => {
    if (categoryFilter !== REQUESTS_CATEGORY_ID || !isPageVisible) {
      return;
    }
    const intervalId = window.setInterval(() => {
      triggerRequestRefresh('auto');
    }, REQUESTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [categoryFilter, isPageVisible, triggerRequestRefresh]);

  useEffect(() => {
    if (
      categoryFilter === REQUESTS_CATEGORY_ID &&
      !wasPageVisibleRef.current &&
      isPageVisible
    ) {
      triggerRequestRefresh('auto');
    }
    wasPageVisibleRef.current = isPageVisible;
  }, [categoryFilter, isPageVisible, triggerRequestRefresh]);

  useEffect(() => {
    setDoneFormId(null);
  }, [requestStatusFilter]);

  useLayoutEffect(() => {
    if (categoryFilter !== REQUESTS_CATEGORY_ID) {
      requestCardPositionsRef.current.clear();
      return;
    }
    const nextPositions = new Map<string, DOMRect>();
    requestCardRefs.current.forEach((node, id) => {
      nextPositions.set(id, node.getBoundingClientRect());
    });

    if (!prefersReducedMotionRef.current && requestCardPositionsRef.current.size > 0) {
      nextPositions.forEach((rect, id) => {
        const node = requestCardRefs.current.get(id);
        if (!node || typeof node.animate !== 'function') {
          return;
        }
        const previous = requestCardPositionsRef.current.get(id);
        if (!previous) {
          node.animate(
            [
              { opacity: 0, transform: 'scale(0.98)' },
              { opacity: 1, transform: 'scale(1)' }
            ],
            { duration: 250, easing: FLIP_ANIMATION_EASING }
          );
          return;
        }
        const deltaX = previous.left - rect.left;
        const deltaY = previous.top - rect.top;
        if (deltaX === 0 && deltaY === 0) {
          return;
        }
        node.animate(
          [
            { transform: `translate(${deltaX}px, ${deltaY}px)` },
            { transform: 'translate(0, 0)' }
          ],
          {
            duration: FLIP_ANIMATION_DURATION_MS,
            easing: FLIP_ANIMATION_EASING
          }
        );
      });
    }

    requestCardPositionsRef.current = nextPositions;
  }, [flowRequests, categoryFilter]);

  useEffect(() => {
    if (withdrawConfirmId === null) {
      return () => {
        /* noop */
      };
    }
    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.forum-request-card__withdraw-button')) {
        return;
      }
      setWithdrawConfirmId(null);
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [withdrawConfirmId]);

  const currentListPath = `${location.pathname}${location.search}${location.hash}`;

  const handleSubmitRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) {
      setRequestError('You must be signed in to suggest a request.');
      return;
    }
    setRequestSubmitting(true);
    setRequestError(null);
    try {
      await api.createFlowNodeRequest({
        title: requestTitle.trim(),
        description: requestDescription.trim()
      });
      setRequestTitle('');
      setRequestDescription('');
      setShowRequestForm(false);
      triggerRequestRefresh();
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setRequestSubmitting(false);
    }
  };

  const toggleRequestVote = async (item: FlowNodeRequest) => {
    if (!user) {
      setRequestError('Sign in to vote on requests.');
      return;
    }
    if (!item.viewerHasUpvoted && item.canUpvote === false) {
      setRequestError('Authors cannot upvote their own requests.');
      return;
    }
    setRequestVoteBusy((current) => ({ ...current, [item.id]: true }));
    setRequestError(null);
    try {
      const response = item.viewerHasUpvoted
        ? await api.removeFlowNodeRequestUpvote(item.id)
        : await api.upvoteFlowNodeRequest(item.id);
      setFlowRequests((current) =>
        orderRequests(
          current.map((request) =>
            request.id === item.id
              ? {
                  ...request,
                  upvoteCount: response.upvoteCount,
                  viewerHasUpvoted: response.viewerHasUpvoted
                }
              : request
          )
        )
      );
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setRequestVoteBusy((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    }
  };

  const handleRequestPageChange = (nextPage: number) => {
    const clamped = Math.max(1, Math.min(nextPage, requestTotalPages));
    setRequestPage(clamped);
  };

  const applyRequestUpdate = useCallback(
    (updated: FlowNodeRequest) => {
      setFlowRequests((current) => {
        const exists = current.some((request) => request.id === updated.id);
        let mapped = exists
          ? current.map((request) => (request.id === updated.id ? updated : request))
          : [...current, updated];
        mapped = mapped.filter(matchesRequestStatusFilter);
        return orderRequests(mapped);
      });
    },
    [matchesRequestStatusFilter, orderRequests]
  );

  const startEditRequest = (item: FlowNodeRequest) => {
    setEditingRequestId(item.id);
    setEditTitle(item.title);
    setEditDescription(item.description);
  };

  const cancelEditRequest = () => {
    setEditingRequestId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const startDoneForm = (item: FlowNodeRequest) => {
    setDoneFormId(item.id);
    setDoneLinkInput(item.marketplaceLink ?? '');
  };

  const cancelDoneForm = () => {
    setDoneFormId(null);
    setDoneLinkInput('');
  };

  const submitDoneForm = async (event: FormEvent, item: FlowNodeRequest) => {
    event.preventDefault();
    await updateRequestStatus(item, 'done', doneLinkInput.trim());
  };

  const submitEditRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingRequestId) {
      return;
    }
    if (!user) {
      setRequestError('You must be signed in to edit requests.');
      return;
    }
    setEditSubmitting(true);
    setRequestError(null);
    try {
      const response = await api.updateFlowNodeRequest(editingRequestId, {
        title: editTitle.trim(),
        description: editDescription.trim()
      });
      applyRequestUpdate(response);
      cancelEditRequest();
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleWithdrawClick = (item: FlowNodeRequest) => {
    if (requestWithdrawBusy[item.id]) {
      return;
    }
    if (withdrawConfirmId === item.id) {
      void confirmWithdrawRequest(item);
      return;
    }
    setWithdrawConfirmId(item.id);
  };

  const confirmWithdrawRequest = async (item: FlowNodeRequest) => {
    if (!user) {
      setRequestError('You must be signed in to withdraw requests.');
      return;
    }
    setRequestWithdrawBusy((current) => ({ ...current, [item.id]: true }));
    setRequestError(null);
    try {
      await api.withdrawFlowNodeRequest(item.id);
      triggerRequestRefresh();
      if (editingRequestId === item.id) {
        cancelEditRequest();
      }
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setRequestWithdrawBusy((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setWithdrawConfirmId(null);
    }
  };

  const updateRequestStatus = async (
    item: FlowNodeRequest,
    nextStatus: 'open' | 'under_development' | 'done',
    marketplaceLink?: string
  ) => {
    if (!user || user.role !== 'admin') {
      setRequestError('Only admins can update request status.');
      return;
    }
    setStatusUpdating((current) => ({ ...current, [item.id]: true }));
    setRequestError(null);
    try {
      const response = await api.markFlowNodeRequestStatus(item.id, {
        status: nextStatus,
        marketplaceLink
      });
      applyRequestUpdate(response);
      triggerRequestRefresh();
      if (nextStatus === 'done') {
        setDoneFormId(null);
        setDoneLinkInput('');
      } else if (nextStatus === 'open' && doneFormId === item.id) {
        setDoneFormId(null);
        setDoneLinkInput('');
      }
    } catch (err) {
      setRequestError((err as Error).message);
    } finally {
      setStatusUpdating((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    }
  };

  const handleCreateThread = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) {
      setError('You must be signed in to create a thread.');
      return;
    }

    const normalizedTitle = newTitle.trim();
    const normalizedContent = newContent.trim();
    if (!normalizedTitle || !normalizedContent) {
      setError('Title and question are required.');
      return;
    }
    if (!newCategoryId) {
      setError('Select a category for your thread.');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const tags = newTags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      const thread = await api.createForumThread({
        title: normalizedTitle,
        content: normalizedContent,
        tags,
        categoryId: newCategoryId,
        ...(user.role === 'admin'
          ? {
              isPinned: newPinned,
              isLocked: newLocked,
              lockReason: newLocked ? newLockReason.trim() || null : null
            }
          : {})
      });
      setNewTitle('');
      setNewContent('');
      setNewTags('');
      setNewPinned(false);
      setNewLocked(false);
      setNewLockReason('');
      setShowComposer(false);
      navigate(`/forum/${thread.id}`, { state: { from: currentListPath } });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const goToPage = (nextPage: number) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
  };

  const handleSelectCategory = (id: 'all' | string) => {
    setPage(1);
    setCategoryFilter(id);
    if (id === REQUESTS_CATEGORY_ID) {
      setShowComposer(false);
    }
    if (id !== REQUESTS_CATEGORY_ID) {
      setShowRequestForm(false);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setAuthorSearch('');
    setStatus('all');
    setPinnedOnly(false);
    setPage(1);
    setCategoryFilter('all');
  };

  const isRequestsView = categoryFilter === REQUESTS_CATEGORY_ID;

  const activeCategory =
    categoryFilter === 'all' || isRequestsView
      ? null
      : findCategoryById(categories, categoryFilter);

  const highlightCount = page === 1 ? Math.min(HIGHLIGHT_COUNT, threads.length) : 0;
  const highlightThreads = useMemo(
    () => threads.slice(0, highlightCount),
    [threads, highlightCount]
  );
  const remainingThreads = useMemo(
    () => (page !== 1 ? threads : threads.slice(highlightCount)),
    [threads, highlightCount, page]
  );

  const categoryPathLabel = isRequestsView
    ? 'Flow & Node Requests'
    : activeCategory
    ? activeCategory.path.map((item) => item.name).join(' / ')
    : 'All discussions';

  const errorMessages = useMemo(() => {
    const messages: string[] = [];
    if (error) {
      messages.push(error);
    }
    if (categoryError) {
      messages.push(categoryError);
    }
    return messages;
  }, [error, categoryError]);

  const renderCategoryItems = (nodes: ForumCategory[], depth = 0): ReactNode[] =>
    nodes.map((category) => {
      const isActive = categoryFilter === category.id;
      return (
        <li key={category.id} className="forum__category-item">
          <button
            type="button"
            className={`forum__category-button${
              isActive ? ' forum__category-button--active' : ''
            }`}
            onClick={() => handleSelectCategory(category.id)}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            {category.name}
          </button>
          {category.children && category.children.length > 0 ? (
            <ul className="forum__category-sublist">
              {renderCategoryItems(category.children, depth + 1)}
            </ul>
          ) : null}
        </li>
      );
    });

  const renderThreadCard = (thread: ForumThreadSummary, variant: 'default' | 'highlight') => {
    const cardClass = [
      'forum-thread-card',
      variant === 'highlight' ? 'forum-thread-card--highlight' : '',
      thread.isPinned ? 'forum-thread-card--pinned' : '',
      thread.isLocked ? 'forum-thread-card--locked' : ''
    ]
      .filter((value) => value.length > 0)
      .join(' ');

    const answered = Boolean(thread.answeredPostId);
    const previewSource = threadPreviews[thread.id];
    const isHovered = hoveredThreadId === thread.id;
    const isLoadingPreview = loadingPreviewId === thread.id;

    const questionPreview = truncatePreview(previewSource?.question ?? '');
    const answerPreview = truncatePreview(previewSource?.answer ?? '');
    const showAnswer =
      isHovered && answered && Boolean(previewSource?.answer) && hoverMode === 'answer';

    let previewText = '';
    let previewIsAnswer = false;

    if (isHovered) {
      if (previewSource) {
        if (showAnswer && answerPreview) {
          previewText = answerPreview;
          previewIsAnswer = true;
        } else {
          previewText = questionPreview || answerPreview || truncatePreview(thread.title ?? '');
          previewIsAnswer = answered && Boolean(answerPreview) && !questionPreview;
        }
      } else if (isLoadingPreview) {
        previewText = 'Loading preview…';
      }
    }

    const showPreview = Boolean(previewText);
    const pillClassName = [
      'forum-thread-card__preview-pill',
      previewIsAnswer ? 'forum-thread-card__preview-pill--answer' : ''
    ]
      .filter(Boolean)
      .join(' ');
    const pillLabel = previewIsAnswer ? 'Answer' : 'Question';

    return (
      <Link
        key={thread.id}
        to={`/forum/${thread.id}`}
        state={{ from: currentListPath }}
        className={cardClass}
        onMouseEnter={() => {
          setHoveredThreadId(thread.id);
          setHoverMode('question');
        }}
        onMouseLeave={() => {
          setHoveredThreadId((current) => (current === thread.id ? null : current));
          setHoverMode('question');
        }}
        onFocus={() => {
          setHoveredThreadId(thread.id);
          setHoverMode('question');
        }}
        onBlur={() => {
          setHoveredThreadId((current) => (current === thread.id ? null : current));
          setHoverMode('question');
        }}
      >
        <div className="forum-thread-card__header">
          <div className="forum-thread-card__title">
            <h2>{thread.title}</h2>
            {!answered && thread.status === 'open' ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--open">
                <span className="material-symbols-outlined">support</span>Open</span>
            ) : null}
            {answered ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--answered">
                <span className="material-symbols-outlined">mark_chat_read</span>Answered
              </span>
            ) : null}
            {!answered && thread.status === 'closed' && !thread.isLocked ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--locked">
                <span className="material-symbols-outlined">close</span>Closed</span>
            ) : null}
            {thread.isPinned ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--pinned">
                <span className="material-symbols-outlined">keep</span> Pinned
              </span>
            ) : null}
            {thread.isLocked ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--locked">
                <span className="material-symbols-outlined">lock</span> Locked
              </span>
            ) : null}
            {thread.madeByThales ? (
              <span className="forum-thread-card__badge forum-thread-card__badge--thales">
                Made by Thales
              </span>
            ) : null}
          </div>
          <div className="forum-thread-card__meta">
            {thread.isLocked && thread.replyCount === 0 ? (
              <span>Read-only announcement</span>
            ) : (
              <>
                <span>Replies: {thread.replyCount}</span>
                <span>Last activity: {new Date(thread.lastActivityAt).toLocaleString()}</span>
              </>
            )}
          </div>
          {thread.category ? (
            <div className="forum-thread-card__category">
              {thread.category.path.map((item, index) => (
                <span key={item.id}>
                  {index > 0 ? ' / ' : ''}
                  {item.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {!(thread.isLocked && thread.replyCount === 0) ? (
          <div className="forum-thread-card__footer">
            {thread.tags.length > 0 ? (
              <div className="forum-thread-card__tags">
                {thread.tags.map((tag) => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="forum-thread-card__author">
              {showPreview ? (
                <div className="forum-thread-card__preview">
                  {previewSource && previewText !== 'Loading preview…' ? (
                    <span className={pillClassName}>{pillLabel}</span>
                  ) : null}
                  <span className="forum-thread-card__preview-text">{previewText}</span>
                </div>
              ) : (
                <>
                  Asked by <strong>{thread.createdByName}</strong> on{' '}
                  {new Date(thread.createdAt).toLocaleString()}
                </>
              )}
            </div>
          </div>
        ) : null}
      </Link>
    );
  };

  return (
    <div className="forum">
      <header className="forum__header">
        <div>
          <h1>Forum</h1>
          <p>Discuss questions, share knowledge, and collaborate with the community.</p>
        </div>
        {!isRequestsView ? (
          <button className="button" type="button" onClick={() => setShowComposer((prev) => !prev)}>
            {showComposer ? 'Cancel new thread' : 'Start a new thread'}
          </button>
        ) : (
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={() => setShowRequestForm((current) => !current)}
                >
                  {showRequestForm ? 'Hide request form' : 'Make a request'}
                </button>
        )}
      </header>

      <div className="forum__layout">
        <aside className="forum__sidebar">
          <div className="forum__sidebar-header">
            <h2>Categories</h2>
            <p>Choose a space to explore conversations or start a new one.</p>
          </div>
          <ul className="forum__category-list">
            <li className="forum__category-item">
              <button
                type="button"
                className={`forum__category-button${
                  categoryFilter === 'all' ? ' forum__category-button--active' : ''
                }`}
                onClick={() => handleSelectCategory('all')}
              >
                All discussions
              </button>
            </li>
            <li className="forum__category-item">
              <button
                type="button"
                className={`forum__category-button${
                  isRequestsView ? ' forum__category-button--active' : ''
                }`}
                onClick={() => handleSelectCategory(REQUESTS_CATEGORY_ID)}
              >
                Flow &amp; Node Requests
              </button>
            </li>
            {renderCategoryItems(categories)}
          </ul>
          <div className="forum__sidebar-footer">
            <button className="button button-secondary" type="button" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        </aside>

        <main className="forum__content">
          {isRequestsView ? (
            <>
              <section className="forum__category-summary">
                <div className="forum__category-title">{categoryPathLabel}</div>
                <p className="forum__category-description">
                  Surface the flows or nodes you need most and vote on upcoming ideas.
                </p>
              </section>

              {showRequestForm ? (
                <form className="forum__request-form" onSubmit={handleSubmitRequest}>
                  <div className="form-field form-field--wide">
                    <label htmlFor="request-title">Title</label>
                    <input
                      id="request-title"
                      type="text"
                      value={requestTitle}
                      onChange={(event) => setRequestTitle(event.target.value)}
                      maxLength={200}
                    />
                  </div>
                  <div className="form-field form-field--full">
                    <label htmlFor="request-description">Details</label>
                    <textarea
                      id="request-description"
                      rows={4}
                      value={requestDescription}
                      onChange={(event) => setRequestDescription(event.target.value)}
                    />
                  </div>
                  <div className="forum__request-form-actions">
                    <button className="button" type="submit" disabled={requestSubmitting}>
                      {requestSubmitting ? 'Submitting…' : 'Publish request'}
                    </button>
                    <button
                      className="button button-tertiary"
                      type="button"
                      onClick={() => setShowRequestForm(false)}
                      disabled={requestSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : null}

              {requestError ? <div className="alert alert-error">{requestError}</div> : null}

              <section className="forum__requests">
                <div className="forum__requests-header">
                  <div>
                    <h2>Community requests</h2>
                    <p>Upvote the work that would unblock you next.</p>
                  </div>
                  <div className="forum__requests-controls">
                    <label htmlFor="request-sort">
                      Sort by
                      <select
                        id="request-sort"
                        value={requestSort}
                        onChange={(event) => {
                          setRequestSort(event.target.value as 'upvotes' | 'recent');
                          setRequestPage(1);
                        }}
                      >
                        <option value="upvotes">Most votes</option>
                        <option value="recent">Newest</option>
                      </select>
                    </label>
                    <label htmlFor="request-search">
                      Search
                      <input
                        id="request-search"
                        type="search"
                        placeholder="Title, description, or author"
                        value={requestSearch}
                        onChange={(event) => {
                          setRequestSearch(event.target.value);
                          setRequestPage(1);
                        }}
                      />
                    </label>
                    <label htmlFor="request-status-filter">
                      Status
                      <select
                        id="request-status-filter"
                        value={requestStatusFilter}
                        onChange={(event) => {
                          setRequestStatusFilter(event.target.value as 'all' | 'active' | 'completed');
                          setRequestPage(1);
                        }}
                      >
                        <option value="all">All statuses</option>
                        <option value="active">Active only</option>
                        <option value="completed">Completed only</option>
                      </select>
                    </label>
                    <label className="checkbox-field">
                      <input
                        type="checkbox"
                        checked={showMineOnly}
                        onChange={(event) => {
                          setShowMineOnly(event.target.checked);
                          setRequestPage(1);
                        }}
                      />
                      My requests
                    </label>
                    {requestPolling ? (
                      <span className="forum__requests-refresh" aria-live="polite">
                        Updating…
                      </span>
                    ) : null}
                  </div>
                </div>

                <div
                  className={`forum__request-list${
                    requestLoading && requestHasLoaded ? ' forum__request-list--busy' : ''
                  }`}
                >
                  {(!requestHasLoaded || (requestLoading && flowRequests.length === 0)) ? (
                    <div className="loading">Loading requests…</div>
                  ) : null}
                  {requestHasLoaded && flowRequests.length === 0 ? (
                    <div className="empty-state">
                      <h3>No requests yet</h3>
                      <p>Be the first to suggest a new flow or node.</p>
                    </div>
                  ) : null}
                  {flowRequests.length > 0 ? (
                    <ul className="forum__request-cards">
                      {flowRequests.map((item) => {
                        const voteBusy = Boolean(requestVoteBusy[item.id]);
                        const withdrawBusy = Boolean(requestWithdrawBusy[item.id]);
                        const confirmPending = withdrawConfirmId === item.id;
                        const isEditing = editingRequestId === item.id;
                        const canVote = item.canUpvote !== false && item.status !== 'done';
                        return (
                          <li
                            key={item.id}
                            ref={registerRequestCard(item.id)}
                            className={`forum-request-card${
                              item.status === 'done'
                                ? ' forum-request-card--done'
                                : item.status === 'under_development'
                                ? ' forum-request-card--under-development'
                                : ''
                            }`}
                          >
                            <div className="forum-request-card__vote">
                              <button
                                type="button"
                                className={`button button-secondary forum-request-card__vote-button${
                                  item.viewerHasUpvoted ? ' forum-request-card__vote-button--active' : ''
                                }`}
                                onClick={() => toggleRequestVote(item)}
                                disabled={voteBusy || withdrawBusy || (!item.viewerHasUpvoted && !canVote)}
                                aria-pressed={item.viewerHasUpvoted}
                              >
                                {voteBusy
                                  ? 'Working…'
                                  : item.viewerHasUpvoted
                                  ? 'Upvoted'
                                  : 'Upvote'}
                              </button>
                              <span className="forum-request-card__vote-count">
                                <AnimatedCounter value={item.upvoteCount} />{' '}
                                {item.upvoteCount === 1 ? 'vote' : 'votes'}
                              </span>
                            </div>
                            <div className="forum-request-card__body">
                      <div className="forum-request-card__meta">
                        <span
                          className={`forum-request-card__kind forum-request-card__kind--${item.kind}`}
                        >
                          {item.kind === 'node' ? 'Node request' : 'Flow request'}
                        </span>
                        <span
                          className={`forum-request-card__status forum-request-card__status--${item.status}`}
                        >
                          {REQUEST_STATUS_LABELS[item.status] ?? 'Open'}
                        </span>
                        <span>
                          Submitted by <strong>{item.createdByName}</strong> ·{' '}
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                        {item.status === 'done' && item.marketplaceLink ? (
                          <a
                            href={item.marketplaceLink}
                            target="_blank"
                            rel="noreferrer"
                            className="forum-request-card__marketplace-link"
                          >
                            View marketplace entry ↗
                          </a>
                        ) : null}
                      </div>
                              {isEditing ? (
                                <form className="forum-request-card__edit" onSubmit={submitEditRequest}>
                                  <div className="form-field form-field--full">
                                    <label htmlFor={`edit-title-${item.id}`}>Title</label>
                                    <input
                                      id={`edit-title-${item.id}`}
                                      type="text"
                                      value={editTitle}
                                      onChange={(event) => setEditTitle(event.target.value)}
                                      maxLength={200}
                                    />
                                  </div>
                                  <div className="form-field form-field--full">
                                    <label htmlFor={`edit-description-${item.id}`}>Details</label>
                                    <textarea
                                      id={`edit-description-${item.id}`}
                                      rows={4}
                                      value={editDescription}
                                      onChange={(event) => setEditDescription(event.target.value)}
                                    />
                                  </div>
                                  <div className="forum-request-card__actions">
                                    <button className="button" type="submit" disabled={editSubmitting}>
                                      {editSubmitting ? 'Saving…' : 'Save changes'}
                                    </button>
                                    <button
                                      className="button button-secondary"
                                      type="button"
                                      onClick={cancelEditRequest}
                                      disabled={editSubmitting}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                <>
                                  <h3>{item.title}</h3>
                                  <p>{item.description}</p>
                                </>
                              )}
                              {item.canEdit && item.status === 'open' && !isEditing ? (
                                <div className="forum-request-card__actions">
                                  <button
                                    type="button"
                                    className="button button-secondary"
                                    onClick={() => startEditRequest(item)}
                                    disabled={editSubmitting || withdrawBusy}
                                  >
                                    Edit
                                  </button>
                                  {item.canWithdraw ? (
                                    <button
                                      type="button"
                                      className={`button button-secondary button-secondary-danger forum-request-card__withdraw-button${
                                        confirmPending ? ' button--confirm' : ''
                                      }`}
                                      onClick={() => handleWithdrawClick(item)}
                                      disabled={withdrawBusy}
                                    >
                                      {withdrawBusy
                                        ? 'Working…'
                                        : confirmPending
                                        ? 'Confirm withdraw'
                                        : 'Withdraw'}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                              {user?.role === 'admin' ? (
                                <div className="forum-request-card__status-actions">
                                  <button
                                    type="button"
                                    className="button button-secondary"
                                    onClick={() =>
                                      updateRequestStatus(
                                        item,
                                        item.status === 'under_development' ? 'open' : 'under_development'
                                      )
                                    }
                                    disabled={Boolean(statusUpdating[item.id])}
                                  >
                                    {statusUpdating[item.id]
                                      ? 'Updating…'
                                      : item.status === 'under_development'
                                      ? 'Mark as open'
                                      : 'Mark under development'}
                                  </button>
                                  {item.status !== 'done' ? (
                                    doneFormId === item.id ? (
                                      <form
                                        className="forum-request-card__done-form"
                                        onSubmit={(event) => submitDoneForm(event, item)}
                                      >
                                        <input
                                          type="url"
                                          placeholder="Marketplace URL"
                                          value={doneLinkInput}
                                          onChange={(event) => setDoneLinkInput(event.target.value)}
                                          required
                                        />
                                        <div className="forum-request-card__actions">
                                          <button className="button" type="submit" disabled={Boolean(statusUpdating[item.id])}>
                                            {statusUpdating[item.id] ? 'Saving…' : 'Save'}
                                          </button>
                                          <button
                                            type="button"
                                            className="button button-tertiary"
                                            onClick={cancelDoneForm}
                                            disabled={Boolean(statusUpdating[item.id])}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </form>
                                    ) : (
                                      <button
                                        type="button"
                                        className="button button-secondary"
                                        onClick={() => startDoneForm(item)}
                                        disabled={Boolean(statusUpdating[item.id])}
                                      >
                                        Mark as done
                                      </button>
                                    )
                                  ) : (
                                    <button
                                      type="button"
                                      className="button button-tertiary"
                                      onClick={() => updateRequestStatus(item, 'open')}
                                      disabled={Boolean(statusUpdating[item.id])}
                                    >
                                      Reopen
                                    </button>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {requestLoading && requestHasLoaded && flowRequests.length > 0 ? (
                    <div className="forum__request-list__overlay" aria-live="polite">
                      Updating requests…
                    </div>
                  ) : null}
                </div>
              {!requestLoading && requestTotalPages > 1 ? (
                <div className="pagination">
                  <div className="pagination__summary">
                    Showing {(requestPage - 1) * requestPageSize + 1}–
                    {Math.min(requestTotalCount, requestPage * requestPageSize)} of {requestTotalCount}
                  </div>
                  <div className="pagination__controls">
                    <div className="pagination__buttons">
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => handleRequestPageChange(1)}
                        disabled={requestPage === 1}
                      >
                        « First
                      </button>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => handleRequestPageChange(requestPage - 1)}
                        disabled={requestPage === 1}
                      >
                        ‹ Prev
                      </button>
                      <span className="pagination__page-indicator">
                        Page {requestPage} of {requestTotalPages}
                      </span>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => handleRequestPageChange(requestPage + 1)}
                        disabled={requestPage === requestTotalPages}
                      >
                        Next ›
                      </button>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => handleRequestPageChange(requestTotalPages)}
                        disabled={requestPage === requestTotalPages}
                      >
                        Last »
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
          </section>
            </>
          ) : (
            <>
              <section className="forum__filters">
                <div className="forum__filter-group">
                  <label htmlFor="forum-search">Search title / tags</label>
                  <input
                    id="forum-search"
                    type="search"
                    placeholder="Search by title or tag"
                    value={search}
                    onChange={(event) => {
                      setPage(1);
                      setSearch(event.target.value);
                    }}
                  />
                </div>
                <div className="forum__filter-group">
                  <label htmlFor="forum-author">Author</label>
                  <input
                    id="forum-author"
                    type="search"
                    placeholder="Search by author name"
                    value={authorSearch}
                    onChange={(event) => {
                      setPage(1);
                      setAuthorSearch(event.target.value);
                    }}
                  />
                </div>
                <div className="forum__filter-group">
                  <label htmlFor="forum-status">Status</label>
                  <select
                    id="forum-status"
                    value={status}
                    onChange={(event) => {
                      setPage(1);
                      setStatus(event.target.value as ForumThreadStatus | 'all');
                    }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="forum__filter-checkboxes">
                  <label className="checkbox-field">
                    <input
                      type="checkbox"
                      checked={pinnedOnly}
                      onChange={(event) => {
                        setPage(1);
                        setPinnedOnly(event.target.checked);
                      }}
                    />
                    Pinned only
                  </label>
                </div>
              </section>

              <section className="forum__category-summary">
                <div className="forum__category-title">{categoryPathLabel}</div>
                {activeCategory?.description ? (
                  <p className="forum__category-description">{activeCategory.description}</p>
                ) : null}
              </section>

              {showComposer ? (
                <section className="forum__composer">
                  <form onSubmit={handleCreateThread}>
                    <div className="form-field">
                      <label htmlFor="new-thread-title">Title</label>
                      <input
                        id="new-thread-title"
                        type="text"
                        value={newTitle}
                        onChange={(event) => setNewTitle(event.target.value)}
                        maxLength={150}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="new-thread-category">Category</label>
                      <select
                        id="new-thread-category"
                        value={newCategoryId}
                        onChange={(event) => setNewCategoryId(event.target.value)}
                        required
                        disabled={flattenedCategories.length === 0}
                      >
                        <option value="">Select a category</option>
                        {flattenedCategories.map((option) => {
                          const prefix = option.depth > 0 ? `${'- '.repeat(option.depth)}` : '';
                          return (
                            <option key={option.id} value={option.id}>
                              {`${prefix}${option.name}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="form-field">
                      <label htmlFor="new-thread-tags">Tags (comma separated)</label>
                      <input
                        id="new-thread-tags"
                        type="text"
                        value={newTags}
                        onChange={(event) => setNewTags(event.target.value)}
                        placeholder="identity, provisioning, reporting"
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="new-thread-content">Question</label>
                      <textarea
                        id="new-thread-content"
                        value={newContent}
                        onChange={(event) => setNewContent(event.target.value)}
                        rows={6}
                        required
                      />
                    </div>
                    {user?.role === 'admin' ? (
                      <div className="forum__composer-admin">
                        <label className="toggle-field">
                          <input
                            type="checkbox"
                            checked={newPinned}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                              setNewPinned(event.target.checked)
                            }
                          />
                          <span>Pin this thread to the top of its category</span>
                        </label>
                        <label className="toggle-field">
                          <input
                            type="checkbox"
                            checked={newLocked}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const checked = event.target.checked;
                              setNewLocked(checked);
                              if (!checked) {
                                setNewLockReason('');
                              }
                            }}
                          />
                          <span>Lock thread (read-only)</span>
                        </label>
                        {newLocked ? (
                          <div className="form-field">
                            <label htmlFor="new-thread-lock-reason">Lock reason (optional)</label>
                            <textarea
                              id="new-thread-lock-reason"
                              value={newLockReason}
                              onChange={(event) => setNewLockReason(event.target.value)}
                              rows={3}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="forum__composer-actions">
                      <button className="button" type="submit" disabled={creating}>
                        {creating ? 'Publishing…' : 'Publish thread'}
                      </button>
                      <button className="button button-secondary" type="button" onClick={() => setShowComposer(false)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {errorMessages.length > 0 ? (
                <div className="alert alert-error">
                  {errorMessages.map((message) => (
                    <div key={message}>{message}</div>
                  ))}
                </div>
              ) : null}

              {highlightThreads.length > 0 ? (
                <section className="forum__highlights">
                  <div className="forum__highlight-header">
                    <h2>Highlights</h2>
                    <span>Latest activity in {categoryPathLabel}</span>
                  </div>
                  <div className="forum__highlight-grid">
                    {highlightThreads.map((thread) => renderThreadCard(thread, 'highlight'))}
                  </div>
                </section>
              ) : null}

              <section className="forum__list">
                {loading ? <div className="loading">Loading threads…</div> : null}
                {!loading && highlightThreads.length === 0 && remainingThreads.length === 0 ? (
                  <div className="empty-state">
                    <h3>No threads yet</h3>
                    <p>Be the first to start a discussion.</p>
                  </div>
                ) : null}
                {!loading && remainingThreads.length > 0 ? (
                  <ul className="forum-thread-list">
                    {remainingThreads.map((thread) => (
                      <li key={thread.id} className="forum-thread-list__item">
                        {renderThreadCard(thread, 'default')}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>

              {!loading && totalPages > 1 ? (
                <div className="pagination">
                  <div className="pagination__summary">
                    Showing {(page - 1) * pageSize + 1}–
                    {Math.min(totalThreads, page * pageSize)} of {totalThreads}
                  </div>
                  <div className="pagination__controls">
                    <label className="pagination__page-size" htmlFor="forum-page-size">
                      Per page
                      <select
                        id="forum-page-size"
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setPage(1);
                        }}
                      >
                        {[10, 20, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="pagination__buttons">
                      <button className="button-tertiary" type="button" onClick={() => goToPage(1)} disabled={page === 1}>
                        « First
                      </button>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                      >
                        ‹ Prev
                      </button>
                      <span className="pagination__page-indicator">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === totalPages}
                      >
                        Next ›
                      </button>
                      <button
                        className="button-tertiary"
                        type="button"
                        onClick={() => goToPage(totalPages)}
                        disabled={page === totalPages}
                      >
                        Last »
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
